<template>
  <div :class="{ 'd-none': hidden }">
    <FormInputButton
      :disabled="disabled"
      :required="required"
      :invalid="invalid && dirty"
      :error="error"
      :label="label"
      :description="description"
      :descriptionType="descriptionType"
      @click="disabled || openRequirementGroupModal()"
    >
      <AppTooltip
        v-if="value"
        placement="top"
        :disabled="!value"
      >
        <section class="break-word">
          <!-- config.showDescription is meant to determine if we show the description inline,
          as opposed to determining if we show it at all -->
          <div v-if="requirementGroupObject.descriptionLong && config.showDescription">{{ value }} - {{ requirementGroupObject.descriptionLong }}</div>
          <div v-else>{{ value }}</div>
        </section>
        <template
          v-if="requirementGroupObject.descriptionLong"
          slot="tooltip"
        >
          <div>{{ requirementGroupObject.descriptionLong || "No Description" }}</div>
        </template>
      </AppTooltip>
      <div
        v-else
        class="text-muted"
      >Select a Group</div>
    </FormInputButton>
    <AppModal
      v-if="modals.requirementGroup"
      :use-dimness="true"
      class="req__group__modal"
      @close="modals.requirementGroup = false"
    >
      <template #modal-header>
        <AppHeading>
          Select Requirement Group
        </AppHeading>
      </template>
      <template #modal-body>
        <div class="d-flex align-items-center mb-3">
          <div class="mr-2">
            <img
              v-if="isLoading"
              src="@/assets/loading-small.gif"
              alt="loading"
              style="width: 24px; height: 24px"
            />
            <AppIcon
              v-else
              class="align-middle"
              name="search"
            />
          </div>
          <FormText
            v-model="searchQuery"
            placeholder="Start typing..."
            class="flex-1"
            @input="searchRequirementGroups"
          />
        </div>
        <AsyncPagination
          v-if="totalCount > pagination.limit"
          :listLength="totalCount"
          :per="pagination.limit"
          :currentPage="pagination.currentPage"
          buttonsClass="justify-content-end"
          @updatePagination="updatePagination"
        />
        <AppLoading v-if="isLoading" />
        <div v-else>
          <section
            v-for="requirementGroup in requirementGroups"
            :key="requirementGroup.id"
            :class="[ 'cursor-pointer item-box w-100 my-2 p-2 rounded-5', requirementGroup.id===value && 'border-primary']"
            @click="selectAndCloseRequirementGroupModal(requirementGroup)"
          >
            <div class="mb-2">
              <AppBadge :pill="true">Group: {{ requirementGroup.id }}</AppBadge>
            </div>
            <div>
              <span class="text-muted">Description</span>
              <!-- The following line handles cases where requirement groups don't have a descriptionLong value,
              which came up in NAU data set -->
              <p>{{ requirementGroup.descriptionLong || requirementGroup.description || "No Description" }}</p>
            </div>
          </section>
          <section v-if="!requirementGroups.length">
            <div> No Requirement Groups found.</div>
          </section>
        </div>
      </template>
      <template #modal-footer>
        <AppButton @click="selectNoneAndCloseModal">Select None</AppButton>
        <AppButton @click="modals.requirementGroup = false">Close</AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { LIMIT_MEDIUM } from '@shared/constants/pagination'
import FormMixin from './FormMixin'
import FormInputButton from './FormInputButton'
import AsyncPagination from '@/components/AsyncPagination'
import FormText from './FormText'
import { mapGetters } from 'vuex'
// eslint-disable-next-line no-restricted-imports
import useRequirementGroups from '@cm/client/composables/useRequirementGroups'
import _debounce from 'lodash/debounce'
import { inject } from '@vue/composition-api'

export default {
  mixins: [ FormMixin ],
  components: {
    FormInputButton,
    AsyncPagination,
    FormText,
  },
  data () {
    return {
      searchQuery: null,
      currentPage: 1,
      limit: LIMIT_MEDIUM,
      isLoading: true,
      requirementGroupObject: {},
      modals: {
        requirementGroup: false,
      },
    }
  },
  setup () {
    const updateRequirementGroup = inject('updateRequirementGroup', () => {})

    const {
      fetchRequirementGroups,
      requirementGroupsAsArray,
      requirementGroupsTotalCount,
    } = useRequirementGroups()

    return {
      updateRequirementGroup,
      getRequirementGroups: fetchRequirementGroups,
      requirementGroups: requirementGroupsAsArray,
      totalCount: requirementGroupsTotalCount,
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    pagination () {
      return {
        currentPage: this.currentPage,
        limit: this.limit,
        skip: (this.currentPage - 1) * this.limit,
      }
    },
  },
  watch: {
    pagination: {
      deep: true,
      async handler () {
        this.isLoading = true
        await this.fetchRequirementGroups()
        this.isLoading = false
      },
    },
  },
  async  created () {
    this.isLoading = true
    await this.fetchRequirementGroup(this.value)
    this.isLoading = false
  },
  methods: {
    debounceSearch: _debounce(async function () {
      await this.fetchRequirementGroups()
      this.isLoading = false
    }, 500),
    async searchRequirementGroups () {
      this.isLoading = true
      this.debounceSearch()
    },
    openRequirementGroupModal () {
      this.modals.requirementGroup = true
      this.isLoading = true
      this.fetchRequirementGroups()
      this.isLoading = false
    },
    setRequirementGroup (data) {
      this.requirementGroupObject = data || {}
      // parent component may need to display data from the requirement group - so we use the provided function to send that value there
      this.updateRequirementGroup(this.requirementGroupObject)
    },
    selectNoneAndCloseModal () {
      const group = { id: '' }
      this.update(group.id)
      this.modals.requirementGroup = false
      this.setRequirementGroup(group)
    },
    selectAndCloseRequirementGroupModal (group = {}) {
      this.update(group.id)
      this.modals.requirementGroup = false
      this.setRequirementGroup(group)
    },
    updatePagination ({ toPage }) {
      this.currentPage = toPage
    },
    async fetchRequirementGroup (val) {
      if (!val) return
      const params = {
        school: this.school,
        list: val,
      }
      const { data = {} } = await this.getRequirementGroups(params)
      this.setRequirementGroup(data[val])

    },
    async fetchRequirementGroups () {
      const params = {
        school: this.school,
        includeCount: true,
        returnFields: 'id,code,description,descriptionLong',
        skip: this.pagination.skip,
        limit: this.pagination.limit,
      }
      if (this.searchQuery) params.searchQuery = this.searchQuery
      await this.getRequirementGroups(params)
    },
  },
}

</script>

<style scoped lang="scss">
@import "~@/css/mixins/transition";
.item-box {
  @include transition(background-color);
  border: 2px solid var(--gray);
}
.item-box:hover {
  background: var(--lighter-gray);
}

.req__group__modal /deep/ .modal-content {
  min-height: 80vh;
  max-height: 80vh;
}
.break-word {
  white-space: break-spaces;
}
</style>
